import { Icon, IconButton } from '@yembo/yemblocks-core';
import { Component, createRef } from 'react';

class AudioPlayer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      canPlay: false,
      isPlaying: false,
      isMute: false,
      duration: 0,
      currentTime: 0,
      timelineMargin: '0px',
      volumeMargin: '28px',
    };
    this.audio = createRef();
    this.playHead = createRef();
    this.timeline = createRef();
    this.volumeSlider = createRef();
    this.volumeHead = createRef();
    this.onPlayHead = false;
    this.TIMELINE_LENGTH = 75;
    this.VOLUME_SLIDER_LENGTH = 40;
    this.HEAD_LENGTH = 8;
    this.SEEK_LENGTH = this.TIMELINE_LENGTH - this.HEAD_LENGTH;
    this.VOLUME_LENGTH = this.VOLUME_SLIDER_LENGTH - this.HEAD_LENGTH;
  }

  componentDidMount() {
    this.audio.current.addEventListener('canplaythrough', this.updateDuration);
    this.audio.current.addEventListener('timeupdate', this.updateTimeline);
    this.timeline.current.addEventListener('click', this.handleTimelineClick);
    this.playHead.current.addEventListener('mousedown', this.handlePlayHeadClick);
    this.volumeSlider.current.addEventListener('click', this.handleVolumeSlider);
    this.volumeHead.current.addEventListener('mousedown', this.handleVolumeHead);
    window.addEventListener('mouseup', this.handleMouseUp);
  }

  componentWillUnmount() {
    this.audio.current.removeEventListener('canplaythrough', this.updateDuration);
    this.audio.current.removeEventListener('timeupdate', this.updateTimeline);
    this.timeline.current.removeEventListener('click', this.handleTimelineClick);
    this.playHead.current.removeEventListener('mousedown', this.handlePlayHeadClick);
    this.volumeSlider.current.removeEventListener('click', this.handleVolumeSlider);
    this.volumeHead.current.removeEventListener('mousedown', this.handleVolumeHead);
    window.removeEventListener('mouseup', this.handleMouseUp);
  }

  handleTimelineClick = (event) => {
    const { duration } = this.state;
    const currentTime = duration * this.getTimelineClickPercent(event);
    this.movePlayHead(event);
    this.audio.current.currentTime = currentTime;
    this.setState({ currentTime });
  };

  handlePlayHeadClick = () => {
    this.onPlayHead = true;
    window.addEventListener('mousemove', this.movePlayHead);
    this.audio.current.removeEventListener('timeupdate', this.updateTimeline);
    this.timeline.current.removeEventListener('click', this.handleTimelineClick);
  };

  handleVolumeSlider = (event) => {
    this.moveVolumeHead(event);
  };

  handleVolumeHead = () => {
    this.onVolumeHead = true;
    window.addEventListener('mousemove', this.moveVolumeHead);
    this.volumeSlider.current.removeEventListener('click', this.handleVolumeSlider);
  };

  handleMouseUp = (event) => {
    const { duration } = this.state;
    if (this.onPlayHead) {
      window.removeEventListener('mousemove', this.movePlayHead);
      const currentTime = duration * this.getTimelineClickPercent(event);
      this.audio.current.currentTime = currentTime;
      this.setState({ currentTime });
      this.timeline.current.addEventListener('click', this.handleTimelineClick);
      this.audio.current.addEventListener('timeupdate', this.updateTimeline);
    }
    if (this.volumeHead) {
      window.removeEventListener('mousemove', this.moveVolumeHead);
      this.volumeSlider.current.addEventListener('click', this.handleVolumeSlider);
    }
    this.onPlayHead = false;
    this.onVolumeHead = false;
  };

  movePlayHead = (event) => {
    const newMarginLeft = event.clientX - this.getPosition(this.timeline.current);
    if (newMarginLeft >= 0 && newMarginLeft <= this.SEEK_LENGTH) {
      this.playHead.current.style.marginLeft = `${newMarginLeft}px`;
    }
    if (newMarginLeft < 0) {
      this.playHead.current.style.marginLeft = '0px';
    }
    if (newMarginLeft > this.SEEK_LENGTH) {
      this.playHead.current.style.marginLeft = `${this.SEEK_LENGTH}px`;
    }
  };

  moveVolumeHead = (event) => {
    const currentVolume = 1 * this.getVolumeClickPercent(event);
    const newMarginLeft = event.clientX - this.getPosition(this.volumeSlider.current);
    let volumeMargin = '0px';
    let isMute = false;
    if (newMarginLeft >= 0 && newMarginLeft <= this.VOLUME_LENGTH) {
      volumeMargin = `${newMarginLeft}px`;
      this.audio.current.volume = currentVolume;
      this.audio.current.muted = isMute;
    }
    if (newMarginLeft < 0) {
      volumeMargin = '0';
      isMute = true;
      this.audio.current.volume = 0;
      this.audio.current.muted = isMute;
    }
    if (newMarginLeft > this.VOLUME_LENGTH) {
      volumeMargin = `${this.VOLUME_LENGTH}px`;
      this.audio.current.volume = 1;
      this.audio.current.muted = isMute;
    }
    this.setState({ volumeMargin, isMute });
  };

  getTimelineClickPercent = (event) => {
    return (event.clientX - this.getPosition(this.timeline.current)) / this.SEEK_LENGTH;
  };

  getVolumeClickPercent = (event) => {
    return (event.clientX - this.getPosition(this.volumeSlider.current)) / this.VOLUME_LENGTH;
  };

  getPosition = (el) => {
    return el.getBoundingClientRect().left;
  };

  updateDuration = () => {
    this.setState({ duration: this.audio.current.duration, canPlay: true });
  };

  formattedTime = (seconds) => {
    let sec = Math.floor(seconds);
    let min = Math.floor(sec / 60);
    min = min >= 10 ? min : `0${min}`;
    sec = Math.floor(sec % 60);
    sec = sec >= 10 ? sec : `0${sec}`;
    return `${min}:${sec}`;
  };

  updateTimeline = () => {
    const { duration } = this.state;
    let seekTime = 0;
    if (this.audio.current.currentTime === parseFloat(duration)) {
      this.setState({
        currentTime: 0,
        timelineMargin: `${seekTime}px`,
        isPlaying: false,
      });
    } else {
      seekTime = this.SEEK_LENGTH * (this.audio.current.currentTime / parseFloat(duration));
      this.setState({
        currentTime: this.audio.current.currentTime,
        timelineMargin: `${seekTime}px`,
      });
    }
  };

  handlePlayButton = () => {
    if (this.audio.current.paused) {
      this.audio.current.play();
      this.setState({ isPlaying: true });
    } else {
      this.audio.current.pause();
      this.setState({ isPlaying: false });
    }
  };
  handleVolumeButton = () => {
    const { isMute } = this.state;
    this.audio.current.muted = !isMute;
    let volumeMargin = '0px';
    if (isMute) {
      // If previous state is mute
      volumeMargin = '28px';
    }
    this.setState({ isMute: !isMute, volumeMargin });
  };

  handleDownload = () => {
    const { onDownload, source } = this.props;
    onDownload(source);
  };

  render() {
    const { isPlaying, isMute, duration, currentTime, timelineMargin, volumeMargin, canPlay } = this.state;
    const { source, downloadQueue } = this.props;
    return (
      <div className='media-audio'>
        <audio id='music' preload='true' src={source} ref={this.audio} />
        <IconButton
          kind='flat'
          className='play play-button'
          isDisabled={!canPlay}
          icon={<Icon name={isPlaying ? 'pause' : 'play'} />}
          onClick={this.handlePlayButton}
        />

        <div id='timeline-text'>
          <div id='seek'>{this.formattedTime(currentTime)}</div>/<div id='duration'>{this.formattedTime(duration)}</div>
        </div>
        <div id='timeline' ref={this.timeline}>
          <div id='play-head' ref={this.playHead} style={{ marginLeft: timelineMargin }}></div>
        </div>
        <div id='volume-container'>
          <IconButton
            kind='flat'
            className='volume volume-button'
            icon={<Icon name={isMute ? 'volume-off' : 'volume-on'} />}
            onClick={this.handleVolumeButton}
          />

          <div id='volume-slider' ref={this.volumeSlider}>
            <div id='volume-head' ref={this.volumeHead} style={{ marginLeft: volumeMargin }}></div>
          </div>
        </div>
        <IconButton
          kind='flat'
          className='download-button download'
          icon={<Icon name='download' />}
          onClick={this.handleDownload}
          isLoading={downloadQueue?.includes(source)}
        />
      </div>
    );
  }
}

export default AudioPlayer;
